import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  Chip,
  Container,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import {
  People as PeopleIcon,
  School as ModulesIcon,
  AdminPanelSettings as AdminIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  LibraryBooks as LibraryBooksIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/auth.context';
import { statisticService } from '../services/statisticService';

// Component tối ưu với thiết kế responsive
const StatCard = ({ title, value, icon, loading = false, color = 'primary' }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        width: { xs: 140, sm: 140, md: 250 },
        borderRadius: 4,
        border: '1px solid',
        borderColor: alpha(theme.palette[color].main, 0.1),
        bgcolor: 'background.paper',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px -10px ${alpha(theme.palette[color].main, 0.3)}`,
          borderColor: alpha(theme.palette[color].main, 0.3),
          '& .stat-icon-bg': {
            transform: 'scale(1.1)',
            bgcolor: alpha(theme.palette[color].main, 0.2),
          }
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box
              className="stat-icon-bg"
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: alpha(theme.palette[color].main, 0.1),
                color: theme.palette[color].main,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {React.cloneElement(icon, { sx: { fontSize: { xs: 20, sm: 24 } } })}
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h4"
              component="div"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: 'text.primary',
                mb: 0.5,
                lineHeight: 1
              }}
            >
              {loading ? (
                <CircularProgress size={24} thickness={5} sx={{ color: theme.palette[color].main }} />
              ) : (
                value?.toLocaleString()
              )}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                opacity: 0.8
              }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [stats, setStats] = useState({
    totalUsers: 0,
    adminUsers: 0,
    totalCourses: 0,
    totalModules: 0,
  });
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Get current user role
        if (currentUser) {
          const idTokenResult = await currentUser.getIdTokenResult();
          setUserRole(idTokenResult.claims.role || 'user');
        }

        // Fetch dashboard statistics
        const statsData = await statisticService.getDashboardStats();

        setStats({
          totalUsers: statsData.totalUsers || 0,
          adminUsers: statsData.adminUsers || 0,
          totalCourses: statsData.totalCourses || 0,
          totalModules: statsData.totalModules || 0
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser]);

  const getUserDisplayName = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName;
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'Admin';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Header matching UserManagement style */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            mb: 4
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "primary.main" }}
            >
              Dashboard
            </Typography>
            <Typography sx={{ mb: 1 }} variant="body1" color="text.secondary">
              Welcome back, {getUserDisplayName()}! Here's what's happening with your platform today.
            </Typography>
          </Box>

          {/* Admin Chip removed */}
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TrendingUpIcon color="primary" />
            Overview Statistics
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={6} sm={6} lg={3}>
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                icon={<PeopleIcon />}
                loading={loading}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={6} lg={3}>
              <StatCard
                title="Admin Users"
                value={stats.adminUsers}
                icon={<AdminIcon />}
                loading={loading}
                color="secondary"
              />
            </Grid>
            <Grid item xs={6} sm={6} lg={3}>
              <StatCard
                title="Total Courses"
                value={stats.totalCourses}
                icon={<SchoolIcon />}
                loading={loading}
                color="info"
              />
            </Grid>
            <Grid item xs={6} sm={6} lg={3}>
              <StatCard
                title="Total Modules"
                value={stats.totalModules}
                icon={<LibraryBooksIcon />}
                loading={loading}
                color="warning"
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}